import { assertNever } from "@impero-com/common/utils/obj";

import { ExistingControlResultPaginatedListDto as ControlResultPaginatedListDto } from "api/generated/model";
import { UserTagCategoryOverview } from "api/generated/model/userTagCategoryOverview";
import { useExistingControlResultsQuery } from "org/controlActivities/queries/useExistingControlResultsQuery";
import { defaultPaginatedListDto } from "org/dashboard/components/dashboardFilters/consts";
import { useTagsQuery } from "org/tags/queries/useTagsQuery";
import { useCurrentUserTimezone } from "utils/currentTimezone";

import { ControlActivitiesScope } from "./consts";
import {
    ControlActivitiesArchivedFilterResult,
    getControlActivitiesArchivedFilter,
} from "./useControlActivitiesArchivedFilter";
import {
    ControlActivitiesDataParamsApi,
    useControlActivitiesDataParams,
} from "./useControlActivitiesDataParams";
import {
    ControlActivitiesDateFilters,
    getControlActivitiesDateFilters,
} from "./useControlActivitiesDateFilters";

const EMPTY_DTO = defaultPaginatedListDto as ControlResultPaginatedListDto;

/** The query params that narrow a fetch to the list's scope. */
interface ControlActivitiesScopeFilters {
    controlProgramIds?: number[];
    controlIds?: string[];
}

export interface ControlActivitiesListData {
    dataParams: ControlActivitiesDataParamsApi;
    dateFilters: ControlActivitiesDateFilters;
    archivedFilter: ControlActivitiesArchivedFilterResult;
    /** `null` until the first load has something to show. */
    dto: ControlResultPaginatedListDto | null;
    tagCategories: UserTagCategoryOverview[];
    isPending: boolean;
    /** A refetch behind rows that are already on screen. */
    isTableLoading: boolean;
}

/**
 * Shared by the activities list and by a single resource's own list.
 * @param scope Scopes the fetch and the persisted view.
 */
export function useControlActivitiesListData(
    scope: ControlActivitiesScope
): ControlActivitiesListData {
    const dataParams = useControlActivitiesDataParams(scope);
    const timezone = useCurrentUserTimezone();
    const {
        dateFilters,
        queryParams: {
            dueDateStart,
            dueDateEnd,
            periodDateStart,
            periodDateEnd,
        },
    } = getControlActivitiesDateFilters(dataParams, scope, timezone);
    const archivedFilter = getControlActivitiesArchivedFilter(dataParams);
    const { archivedStatus, isEnabled } = archivedFilter;

    // Narrowed by the backend rather than client-side, so a scoped list never
    // pulls every activity in the org.
    const scopeFilters = ((): ControlActivitiesScopeFilters => {
        switch (scope.type) {
            case "all":
                return {};
            case "controlProgram":
                return { controlProgramIds: [scope.controlProgramId] };
            case "control":
                return { controlIds: [scope.controlId] };
            default:
                return assertNever(scope);
        }
    })();

    const params = {
        limit: 10000,
        archivedStatus,
        enabled: isEnabled,
        dueDateStart,
        dueDateEnd,
        periodDateStart,
        periodDateEnd,
        classificationId: dataParams.values.classificationId,
        ...scopeFilters,
    };

    const {
        data: controlResultsDto,
        hasNextPage,
        isFetching,
        isLoading,
        fetchNextPage,
    } = useExistingControlResultsQuery(params);
    // Force the fetch of the whole dataset
    if (hasNextPage && !isFetching) {
        void fetchNextPage();
    }

    const { isLoading: isTagCategoriesLoading, data: tags } = useTagsQuery();

    const dto = archivedFilter.isEnabled ? controlResultsDto : EMPTY_DTO;

    return {
        dataParams,
        dateFilters,
        archivedFilter,
        dto: dto ?? null,
        tagCategories: tags?.tagCategories ?? [],
        // Disabled filters short-circuit to an empty dto and the query throws on
        // failure, so pending is the only state left without data.
        isPending: isLoading || isTagCategoriesLoading || !dto,
        isTableLoading: archivedFilter.isEnabled && (isFetching || hasNextPage),
    };
}
